import {
  useState, useEffect, useMemo,
} from 'react';
import {
  Form, Input,
} from '@skyline-platform/alchemy';
import { FormFooter } from '../FormFooter/FormFooter';
import PropTypes from 'prop-types';
import { validateStringLength } from '@utils/formValidators';
import * as is from 'is';
import { validateIPOrFQDN } from '@utils/formValidators';
import { updateAppliance } from '@utils/graphql/creators/appliances';
import { getAppliances } from '@utils/graphql/creators/appliances';
import { useDebouncedCallback } from 'use-debounce';
import '../FullScreenForm.css';
import { InfoIcon } from '@components/InfoIcon/InfoIcon';
import { compareData } from '@utils/objectHandler';
import { SearchExistingSource } from '../SourceForm/SearchExistingSource';
import { notifications } from '@utils/notifications/index';
import { getLogger } from '@utils/general';
import { getStreamProfileLabel } from '@pages/utils/helpers';
import { IPv4 } from '@pages/split/utils/Constants';

const logger = getLogger('src.components.ApplianceForm');

export const ApplianceForm = ({
  applianceData,
  handleClose,
}) => {
  const [name, setName] = useState(applianceData?.name || '');
  const [location, setLocation] = useState(applianceData?.location || '');
  const [publicAddress, setPublicAddress] = useState(applianceData?.public_address || '');
  const [duplicateName, setDuplicateName] = useState(false);
  const offlineProfLabel = getStreamProfileLabel(applianceData?.offline_source);
  // This is needed to re-populate the offline source when editing an appliance
  const offlineProfile = useMemo(() => {
    if (applianceData.offline_source) {
      return { ...applianceData.offline_source, name: offlineProfLabel };
    }
    return undefined;
  }, [applianceData.offline_source, offlineProfLabel]);

  // State for user-selected offline profile
  const [selectedOfflineProfile, setSelectedOfflineProfile] = useState(offlineProfile);

  // Sync selectedOfflineProfile when offlineProfile changes (e.g., editing a different appliance)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern, sync update is intentional
    setSelectedOfflineProfile(offlineProfile);
  }, [offlineProfile]);

  const checkDuplicateApplianceName = async (name) => {
    const { query, execute } = getAppliances(null, { name });
    query.addReturnProperties('id', 'name');

    await execute()
      .then(({ error, data }) => {
        if (error) {
          logger.error(error);
        }
        if (data && data.length > 0) {
          setDuplicateName(true);
        } else {
          setDuplicateName(false);
        }
      })
      .catch(() => {
        logger.error(
          `There was a failure retrieving data for appliance: ${name}`
        );
      });
  };

  const nameValid = useMemo(() => {
    if (is.empty(name)) return { isValid: false, error: null };
    if (duplicateName) return {
      isValid: false, error: 'Appliance name already exists. Please update the appliance name.',
    };
    return validateStringLength(name, 1, 255, 'Name');
  }, [name, duplicateName]);

  const publicAddressValid = useMemo(() => {
    if (is.empty(publicAddress)) return { isValid: true, error: null };
    return validateIPOrFQDN(publicAddress, IPv4, 'Public');
  }, [publicAddress]);

  const error = useMemo(() => ({
    name: { isValid: nameValid.isValid, error: nameValid.error },
    publicAddress: { isValid: publicAddressValid.isValid, error: publicAddressValid.error },
  }), [nameValid, publicAddressValid]);

  const allFieldsValid = useMemo(() => {
    return nameValid.isValid && publicAddressValid.isValid;
  }, [nameValid, publicAddressValid]);

  const debouncedValidateApplianceName = useDebouncedCallback(checkDuplicateApplianceName, 300);
  useEffect(() => {
    if (name.length === 0 || applianceData?.name === name) {
      setTimeout(() => setDuplicateName(false), 0);
    } else {
      debouncedValidateApplianceName(name, setDuplicateName);
    }
  }, [name]);

  const handleAppliance = async () => {
    const updatedVals = {
      name,
      location,
      offline_source_id: selectedOfflineProfile?.id ?? null,
      public_address: publicAddress === '' ? null : publicAddress,
    };

    /**
     * Leaving this because this is technically the correct way to  see if the
     * offline source has changed, but adding this causes an error with SSOT
     * if the user changes nothing and presses the Update Appliance button
     * because technically nothing was updated
     */
    // const originalAppliance = {
    //   ...applianceData,
    //   offline_source_id: applianceData?.offline_source?.id ?? null,
    // };

    const updatedData = compareData(applianceData, updatedVals);
    const { mutation, execute } = updateAppliance('update_appliance', applianceData.id, updatedData);
    mutation.addReturnProperties('id', 'name');
    const updatedApplianceName = updatedData.name || applianceData.name;

    let appliance;
    const failMessage = 'Failed to edit appliance.';
    await execute()
      .then(({
        error, data,
      }) => {
        if (error) {
          notifications.error(failMessage);
          logger.error(error, failMessage);
        }
        if (data) {
          appliance = data;
          notifications.success(`Successfully updated appliance ${updatedApplianceName}.`);
        }
      })
      .catch((error) => {
        notifications.error(failMessage);
        logger.error(error, failMessage);
      });

    return appliance;
  };

  return (
    <gar-appliance-form>
      <Form
        formTitle='Edit Appliance'
        footerPlacement='right'
        footer={[
          <FormFooter
            key='footer'
            onCancel={handleClose}
            onSubmit={async () => {
              await handleAppliance();
              handleClose();
            }}
            submitLabel='Update Appliance'
            isValid={allFieldsValid}
          />,
        ]}
      >
        <Input
          id='appliance-name'
          inputLabel='Name'
          helperText='Name of the Appliance'
          onChange={(e) => { setName(e.target.value); }}
          value={name}
          type='text'
          required
          errorMessage={error.name.isValid ? '' : error.name.error}
        />
        <Input
          id='appliance-location'
          inputLabel='Location'
          helperText='Location of the Appliance'
          onChange={(e) => { setLocation(e.target.value); }}
          value={location}
          type='text'
        />
        <Input
          id='input-public-addr'
          inputLabel='Public Address'
          labelAdornment={
            <InfoIcon
              id='public-address'
              label='Show description for Public Address'
              desc='The public address to use to connect to the server if the management adress is behind a NAT.'
            />
          }
          helperText='IPv4 address or FQDN'
          value={publicAddress}
          onChange={(e) => setPublicAddress(e.target.value)}
          error={error.publicAddress.isValid ? undefined : 'true'}
          errorMessage={error.publicAddress.isValid ? '' : error.publicAddress.error}
        />
        <SearchExistingSource
          formData={selectedOfflineProfile}
          sourceCategory='Offline'
          // set offline source data to value after user makes selection
          setFormData={setSelectedOfflineProfile}
        />
      </Form>
    </gar-appliance-form>
  );
};

ApplianceForm.propTypes = {
  applianceData: PropTypes.object,
  handleClose: PropTypes.func,
  setFormData: PropTypes.func,
};